/*

FILE: animations.js
DESCRIPTION: Animation timelines/sequences
AUTHOR(S): Nick Katarow

*/

import anime from 'animejs/lib/anime.es.js';
import initCustomers from './customers.js';

/**
 * @function
 * @description Changes opacity to zero
 * - Element should have opacity set to zero before this is triggered
 * */
const popIn = (el) => {
    anime({
        targets: el,
        opacity: 1,
        duration: 250,
        scale: [
            { value: 0.9, duration: 0 },
            { value: 1, duration: 250 },
        ],
        translateY: [
            { value: 20, duration: 0 },
            { value: 0, duration: 250 },
        ],
        easing: 'easeOutSine',
    });
};

/**
 * @function
 * @description Changes opacity to zero
 * - Element should have opacity set to zero before this is triggered
 * */
const fadeIn = (el) => {
    anime({
        targets: el,
        opacity: 1,
        duration: 250,
        easing: 'easeOutSine',
    });
};

/**
 * @function
 * @description Changes opacity to one and moves element up 20px
 * - Element should have opacity set to zero before this is triggered
 * */
const fadeInUp = (el) => {
    anime({
        targets: el,
        opacity: 1,
        translateY: [
            { value: 20, duration: 0 },
            { value: 0, duration: 250 },
        ],
        duration: 250,
        easing: 'easeOutSine',
    });
};

/**
 * @function
 * @description Changes opacity to zero
 * */
const fadeOut = (el) => {
    anime({
        targets: el,
        opacity: 0,
        duration: 250,
        easing: 'easeOutSine',
    });
};

/**
 * @function
 * @description Sends play event to video
 * */
const videoTrigger = (el) => {
    const video = el.querySelectorAll('.stacked-images')[0];
    video.play();
    video.classList.add('has-played');
};

/**
 * @function
 * @description Same as fadeInUp but does so to each individual element held within the container with data-animation-type="sequence".
 * */
const sequence = (el) => {
    const targets = Array.from(el.children);

    anime({
        targets,
        opacity: 1,
        translateY: -10,
        easing: 'easeOutSine',
        duration: 250,
        delay: anime.stagger(250), // increase delay by 250ms for each element
    });
};

/**
 * @function
 * @description Animates numerical value up from 0 to value in passed element
 * */
const count = (el) => {
    let roundTo = 1;
    const countToValue = el.innerHTML;
    const decimalPlaces = countToValue.split('.');

    // If number has a decimal, find how many and use for rounding during the animation
    if (decimalPlaces.length > 1) {
        if (decimalPlaces[1].length === 1) {
            roundTo = 10;
        } else {
            roundTo = 100;
        }
    }

    anime({
        targets: el,
        innerHTML: [0, countToValue],
        easing: 'easeOutSine',
        round: roundTo,
        duration: 1500,
        complete() {
            // Since we don't want a rounded final result, replace with original value
            el.innerHTML = countToValue;
        },
    });
};

/**
 * @function
 * @description Routes an element with data-animation-type to appropriate animation function
 * @param {object} el - Element that has the data-animation-type attribute, is passed to all other functions in this file
 * */
const scrollElementEntrance = (el) => {
    const { animationType } = el.dataset;
    const customFn = el.dataset.animationInit;

    switch (animationType) {
    case 'pop-in':
        popIn(el);
        break;
    case 'fade-in':
        fadeIn(el);
        break;
    case 'fade-in-up':
        fadeInUp(el);
        break;
    case 'fade-out':
        fadeOut(el);
        break;
    case 'video-trigger':
        videoTrigger(el);
        break;
    case 'sequence':
        sequence(el);
        break;
    case 'count-up':
        count(el);
        break;
    default:
        fadeIn(el);
    }

    if (customFn) {
        switch (customFn) {
        case 'logo-shift-horiz':
            initCustomers(el);
            break;
        case 'multi-stats':
            el.querySelectorAll('.stat').forEach((stat) => {
                count(stat);
            });
            break;
        default:
            break;
        }
    }
};

export {
    scrollElementEntrance, fadeIn, fadeOut, videoTrigger,
};
